import React from 'react';
import { BookOpen, BarChart3, Users, GraduationCap, ListChecks, Smartphone, HeartHandshake } from 'lucide-react';
import { HcLogo } from './HcLogo';

const HERO = 'https://images.unsplash.com/photo-1758521541409-256fa9e24fe4?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400';

const Pillar = ({ icon: Icon, title, text }) => (
  <div className="text-center">
    <div className="mx-auto w-16 h-16 rounded-full bg-[#F6EAF5] flex items-center justify-center mb-2">
      <Icon className="w-7 h-7 text-[#922A8C]" />
    </div>
    <div className="font-bold text-[#363636]">{title}</div>
    <div className="text-xs text-gray-500 leading-snug">{text}</div>
  </div>
);

const Feature = ({ icon: Icon, label }) => (
  <div className="flex flex-col items-center text-center px-2">
    <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center shadow-sm mb-2">
      <Icon className="w-5 h-5 text-[#922A8C]" />
    </div>
    <span className="text-sm font-semibold text-[#363636] leading-tight">{label}</span>
  </div>
);

export const HcAuthShell = ({ title, subtitle, intro, children, showPillars = true }) => (
  <div className="hc-root min-h-screen grid lg:grid-cols-2 bg-white">
    <div className="relative flex flex-col justify-center px-6 sm:px-12 xl:px-20 py-10 overflow-hidden">
      <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#F6EAF5]" aria-hidden />
      <div className="absolute -bottom-28 -left-16 w-64 h-64 rounded-full bg-[#F6EAF5]/70" aria-hidden />
      <div className="relative w-full max-w-lg mx-auto hc-fade-up">
        <div className="flex justify-center mb-10"><HcLogo size="lg" /></div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-[#363636]" data-testid="hc-auth-title">{title}</h1>
        {subtitle && <p className="text-lg font-semibold text-[#922A8C] mt-1">{subtitle}</p>}
        {intro && <p className="text-gray-600 mt-3 mb-6">{intro}</p>}
        {children}
        {showPillars && (
          <div className="grid grid-cols-3 gap-4 mt-10">
            <Pillar icon={BookOpen} title="Learn" text="Gain new skills at your own pace" />
            <Pillar icon={BarChart3} title="Grow" text="Build your future with confidence" />
            <Pillar icon={Users} title="Succeed" text="We're here to help every step of the way" />
          </div>
        )}
      </div>
    </div>
    <div className="relative hidden lg:block">
      <img src={HERO} alt="Participant learning on a laptop" className="absolute inset-0 w-full h-full object-cover object-[75%_center]" />
      <div className="absolute top-10 right-10 max-w-xs bg-white/85 backdrop-blur-md rounded-2xl p-6 border-l-4 border-[#922A8C] shadow-lg">
        <div className="text-4xl leading-none text-[#922A8C] font-extrabold">&ldquo;</div>
        <p className="text-lg text-[#363636]">Every step you take today is a step toward a brighter tomorrow.</p>
      </div>
      <div className="absolute bottom-10 left-10 right-10 bg-white/80 backdrop-blur-md rounded-2xl p-6 grid grid-cols-4 divide-x divide-gray-200 shadow-lg">
        <Feature icon={GraduationCap} label="Your courses in one place" />
        <Feature icon={ListChecks} label="Pick up where you left off" />
        <Feature icon={Smartphone} label="Learn on any device" />
        <Feature icon={HeartHandshake} label="Support from your case manager" />
      </div>
    </div>
  </div>
);
